import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Media from 'react-media'
import Link from 'gatsby-link'

import Adobe from '@/components/icons/brands/adobe'
import Apple from '@/components/icons/brands/apple'
import Arrow from '@/components/icons/arrow'
import Bumble from '@/components/icons/brands/bumble'
import Facebook from '@/components/icons/brands/facebook'
import GetStartedButton from '@/components/header/get-started-login-button'
import Groupme from '@/components/icons/brands/groupme'
import Instagram from '@/components/icons/brands/instagram'
import Kika from '@/components/icons/brands/kika'
import Mailchimp from '@/components/icons/brands/mailchimp'
import Paypal from '@/components/icons/brands/paypal'
import Pivo from '@/components/icons/brands/pivo'
import Samsung from '@/components/icons/brands/samsung'
import ShapesImage from '@/images/shapes/header.svg'
import Signal from '@/components/icons/brands/signal'
import Slack from '@/components/icons/brands/slack'
import Snap from '@/components/icons/brands/snap'
import Spotify from '@/components/icons/brands/spotify'
import Tiktok from '@/components/icons/brands/tiktok'
import Twitter from '@/components/icons/brands/twitter'
import WhatsApp from '@/components/icons/brands/whatsapp'
import { containerWidth, containerPadding } from '@/components/constants'
import { desktop, mobile } from '@/components/utils/media-query'

import Marquee from '../marquee'
import Shapes from '../shapes'

import HeaderVideo from './video'

const ShapesHeader = styled(Shapes)`
  width: 100vw;
  height: calc(100vh + 500px);
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  margin-top: -116px;
`

const HeaderContainer = styled.div`
  ${desktop.css`
        display: flex;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
        height: calc(100vh - 116px);
        max-height: 800px;
        min-height: 625px;
        mix-blend-mode: difference;
        position: relative;
    `}

  ${mobile.css`
        align-items: flex-start;
        margin: auto;
        height: calc(100vh - 72px);
    `}
`
const Inner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  ${mobile.css`
        overflow: hidden;
        width: 100vw;
        height: 100%;
    `}
`

const Container = styled.div`
  ${desktop.css`
        width: ${containerWidth}px;
        display: flex;
        height: 100%;
        position: relative;
        align-items: center;
    `}

  ${mobile.css`
        position: relative;
        width: 100vw;
        overflow: hidden;
        flex: 1;
        display: flex;
        flex-direction: column;
        z-index: 1;
        height: 100%;
    `}
`

const BlurbContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 75px);
`

const Blurb = styled.div`
  ${desktop.css`
        margin-top: 100px;
        position: relative;
        z-index: 1;
        width: 640px;
        flex-shrink: 0;
        padding: 0 ${containerPadding}px 280px 32px;

        p {
            width: 75%;
            margin: 0 0 30px;
            font-size: 20px;
            line-height: 28px;
        }

        h1 {
            width: 80%;
            margin-bottom: 20px;
        }
    `}

  ${mobile.css`
        box-sizing: border-box;
        flex-shrink: 1;
        width: 100%;
        padding: 0 15px;
        margin-top: 15%;
        margin-bottom: 15%;

        h1 {
            margin-bottom: 15px;
            line-height: 38px;
            font-size: 34px;
        }

        p {
            margin-bottom: 15px;
        }
    `}
`

const Buttons = styled.div`
  display: flex;
  align-items: center;

  ${mobile.css`
        flex-direction: column;
        align-items: flex-start;
    `}
`

const MarqueeContainer = styled.div`
  ${desktop.css`
        width: 100%;
        position: absolute;
        bottom: 50px;
        align-self: flex-end;
    `}

  ${mobile.css`
        position: relative;
        display: flex;
        flex-direction: column;
        z-index: 1;
        bottom: 30px;
    `}
`

const ScrollMore = styled.div`
  align-self: center;
  transform: translateY(-50%);
`

const ExtendedLink = styled(Link)`
  border-bottom: none;
  color: white;
`

const Header = () => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  return mounted ? (
    <section>
      <Media query={desktop.query}>
        {(matches) => (
          <HeaderContainer>
            {matches && <ShapesHeader source={ShapesImage} top="-90px" />}
            <Inner>
              {!matches ? (
                <Container>
                  <HeaderVideo />
                  <BlurbContainer>
                    <Blurb>
                      <h1>Start building with the power of GIPHY</h1>
                      <p>
                        GIPHY SDK helps you integrate the world&#39;s largest GIF library, customize
                        a suite of powerful features, and boost engagement with just a few lines of
                        code.
                      </p>
                      <Buttons>
                        <ExtendedLink to={'/docs/sdk'}>
                          <GetStartedButton />
                        </ExtendedLink>
                      </Buttons>
                    </Blurb>
                    <MarqueeContainer>
                      <Marquee>
                        <Instagram small={!matches} />
                        <Apple small={!matches} />
                        <Snap small={!matches} />
                        <Twitter small={!matches} />
                        <Samsung small={!matches} />
                        <Facebook small={!matches} />
                        <Adobe small={!matches} />
                        <Tiktok small={!matches} />
                        <Paypal small={!matches} />
                        <Slack small={!matches} />
                        <Bumble small={!matches} />
                        <Signal small={!matches} />
                        <WhatsApp small={!matches} />
                        <Groupme small={!matches} />
                        <Mailchimp small={!matches} />
                        <Kika small={!matches} />
                        <Pivo small={!matches} />
                        <Spotify small={!matches} />
                      </Marquee>
                      {!matches && (
                        <ScrollMore>
                          <Arrow />
                        </ScrollMore>
                      )}
                    </MarqueeContainer>
                  </BlurbContainer>
                </Container>
              ) : (
                <>
                  <Container>
                    <Blurb>
                      <h1>Start building with the power of GIPHY</h1>
                      <p>
                        GIPHY SDK helps you integrate the world&#39;s largest GIF library, customize
                        a suite of powerful features, and boost engagement with just a few lines of
                        code.
                      </p>
                      <Buttons>
                        <ExtendedLink to={'/docs/sdk'}>
                          <GetStartedButton />
                        </ExtendedLink>
                      </Buttons>
                    </Blurb>
                    <HeaderVideo />
                  </Container>
                  <MarqueeContainer>
                    <Marquee>
                      <Instagram small={!matches} />
                      <Apple small={!matches} />
                      <Snap small={!matches} />
                      <Twitter small={!matches} />
                      <Samsung small={!matches} />
                      <Facebook small={!matches} />
                      <Adobe small={!matches} />
                      <Tiktok small={!matches} />
                      <Paypal small={!matches} />
                      <Slack small={!matches} />
                      <Bumble small={!matches} />
                      <Signal small={!matches} />
                      <WhatsApp small={!matches} />
                      <Groupme small={!matches} />
                      <Mailchimp small={!matches} />
                      <Kika small={!matches} />
                      <Pivo small={!matches} />
                      <Spotify small={!matches} />
                    </Marquee>
                    {!matches && (
                      <ScrollMore>
                        <Arrow />
                      </ScrollMore>
                    )}
                  </MarqueeContainer>
                </>
              )}
            </Inner>
          </HeaderContainer>
        )}
      </Media>
    </section>
  ) : null
}

export default Header
